import { useState, useCallback } from 'react';
import { Home, CheckCircle2, ChevronLeft, ChevronRight, Upload, Calculator, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

/* ── Shared mini-components (self-contained) ─────────────────────────────── */

function Field({ label, required, error, children }) {
    return (
        <div className="flex flex-col gap-1.5">
            <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {label}{required ? <span className="text-rose-500 ml-0.5">*</span> : null}
            </Label>
            {children}
            {error ? (
                <p className="text-xs text-rose-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />{error}
                </p>
            ) : null}
        </div>
    );
}

function FormInput({ id, type, label, placeholder, value, onChange, required }) {
    return (
        <Field label={label} required={required}>
            <Input id={id} type={type || 'text'} placeholder={placeholder} value={value} onChange={onChange} required={required}
                className="h-11 rounded-xl border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all" />
        </Field>
    );
}

function FormSelect({ id, label, options, value, onChange, required }) {
    return (
        <Field label={label} required={required}>
            <select id={id} value={value} onChange={onChange} required={required}
                className="h-11 rounded-xl border px-3 text-sm w-full outline-none border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all">
                <option value="">Select…</option>
                {options.map(function (o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
            </select>
        </Field>
    );
}

function FileUploadField({ label, accept, onChange, file }) {
    return (
        <div className="flex flex-col gap-1.5">
            <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</Label>
            <label className="flex flex-col items-center justify-center gap-2 h-24 border-2 border-dashed rounded-xl cursor-pointer border-slate-200 dark:border-slate-700 hover:border-emerald-400 bg-slate-50 dark:bg-slate-800/50 hover:bg-emerald-50/50 transition-all group">
                <input type="file" accept={accept} className="sr-only" onChange={onChange} />
                {file ? (
                    <div className="flex items-center gap-2 px-3">
                        <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
                        <span className="text-xs text-emerald-600 font-medium truncate max-w-[160px]">{file.name}</span>
                    </div>
                ) : (
                    <div className="flex flex-col items-center gap-1">
                        <Upload className="h-5 w-5 text-slate-400 group-hover:text-emerald-500 transition-colors" />
                        <span className="text-xs text-slate-500">Click to upload</span>
                        <span className="text-[10px] text-slate-400">{accept}</span>
                    </div>
                )}
            </label>
        </div>
    );
}

function EMICalc() {
    var sa = useState(500000); var amount = sa[0]; var setAmount = sa[1];
    var sr = useState(8.5); var rate = sr[0]; var setRate = sr[1];
    var st = useState(20); var tenure = st[0]; var setTenure = st[1];

    var emi = useCallback(function () {
        var r = rate / 12 / 100, n = tenure * 12;
        if (!amount || !r || !n) return 0;
        return (amount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    }, [amount, rate, tenure]);

    var emiVal = emi();
    var totalPayable = emiVal * tenure * 12;
    var totalInterest = totalPayable - amount;

    function fmt(n) {
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
    }

    return (
        <div className="rounded-2xl border border-emerald-100 dark:border-emerald-900/40 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/10 p-5 space-y-4">
            <div className="flex items-center gap-2">
                <Calculator className="h-4 w-4 text-emerald-600" />
                <h4 className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">EMI Calculator</h4>
            </div>
            <div className="space-y-3">
                <div className="space-y-1">
                    <div className="flex justify-between text-xs text-slate-600 dark:text-slate-400"><span>Loan Amount</span><span className="font-medium">{fmt(amount)}</span></div>
                    <input type="range" min="100000" max="10000000" step="50000" value={amount} onChange={function (e) { setAmount(Number(e.target.value)); }} className="w-full accent-emerald-600 h-1.5 cursor-pointer" />
                </div>
                <div className="space-y-1">
                    <div className="flex justify-between text-xs text-slate-600 dark:text-slate-400"><span>Interest Rate</span><span className="font-medium">{rate}% p.a.</span></div>
                    <input type="range" min="5" max="20" step="0.1" value={rate} onChange={function (e) { setRate(Number(e.target.value)); }} className="w-full accent-emerald-600 h-1.5 cursor-pointer" />
                </div>
                <div className="space-y-1">
                    <div className="flex justify-between text-xs text-slate-600 dark:text-slate-400"><span>Tenure</span><span className="font-medium">{tenure} yrs</span></div>
                    <input type="range" min="1" max="30" step="1" value={tenure} onChange={function (e) { setTenure(Number(e.target.value)); }} className="w-full accent-emerald-600 h-1.5 cursor-pointer" />
                </div>
            </div>
            <div className="grid grid-cols-3 gap-2 pt-2 border-t border-emerald-200 dark:border-emerald-800">
                <div className="text-center"><p className="text-[10px] text-slate-500 uppercase">Monthly EMI</p><p className="text-sm font-bold text-emerald-600">{fmt(emiVal)}</p></div>
                <div className="text-center border-x border-emerald-200 dark:border-emerald-800"><p className="text-[10px] text-slate-500 uppercase">Total Interest</p><p className="text-sm font-bold text-slate-700 dark:text-slate-300">{fmt(totalInterest)}</p></div>
                <div className="text-center"><p className="text-[10px] text-slate-500 uppercase">Total Payable</p><p className="text-sm font-bold text-slate-700 dark:text-slate-300">{fmt(totalPayable)}</p></div>
            </div>
        </div>
    );
}

function ProgressBar({ steps, current }) {
    var pct = steps.length > 1 ? Math.round((current / (steps.length - 1)) * 100) : 100;
    return (
        <div className="w-full">
            <div className="flex items-start justify-between mb-6">
                {steps.map(function (step, i) {
                    var done = i < current, active = i === current;
                    var cc = done ? 'bg-emerald-500 border-emerald-500 text-white' : active ? 'bg-white dark:bg-slate-900 border-emerald-500 text-emerald-600' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-400';
                    var lc = active ? 'text-emerald-600 dark:text-emerald-400' : done ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400';
                    return (
                        <div key={i} className="flex-1 flex flex-col items-center relative">
                            {i < steps.length - 1 ? <div className={'absolute top-4 left-1/2 w-full h-0.5 transition-all duration-500 ' + (done ? 'bg-emerald-500' : 'bg-slate-200 dark:bg-slate-700')} /> : null}
                            <div className={'relative z-10 h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all duration-500 ' + cc}>
                                {done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                            </div>
                            <span className={'mt-2 text-[10px] font-medium text-center leading-tight max-w-[70px] ' + lc}>{step}</span>
                        </div>
                    );
                })}
            </div>
            <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-700" style={{ width: pct + '%' }} />
            </div>
            <div className="flex justify-between mt-1">
                <span className="text-[10px] text-slate-400">Step {current + 1} of {steps.length}</span>
                <span className="text-[10px] text-emerald-600 font-semibold">{pct}% Complete</span>
            </div>
        </div>
    );
}

/* ── Step content components ─────────────────────────────────────────────── */

function Step1({ d, u }) {
    function c(k) { return { value: d[k], onChange: function (e) { u(k, e.target.value); } }; }
    return (
        <div className="space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
                <FormInput id="fullName" label="Full Name" placeholder="Ravi Kumar" required {...c('fullName')} />
                <FormInput id="mobile" label="Mobile Number" type="tel" placeholder="+91 98765 43210" required {...c('mobile')} />
                <FormInput id="email" label="Email Address" type="email" placeholder="ravi@example.com" required {...c('email')} />
                <FormInput id="dob" label="Date of Birth" type="date" required {...c('dob')} />
                <FormInput id="pan" label="PAN Number" placeholder="ABCDE1234F" required {...c('pan')} />
                <FormInput id="aadhaar" label="Aadhaar Number" placeholder="1234 5678 9012" required {...c('aadhaar')} />
            </div>
            <FormSelect id="empType" label="Employment Type" required options={[{ value: 'salaried', label: 'Salaried' }, { value: 'self_employed', label: 'Self Employed' }]} {...c('employmentType')} />
        </div>
    );
}

function Step2({ d, u }) {
    function c(k) { return { value: d[k], onChange: function (e) { u(k, e.target.value); } }; }
    return (
        <div className="grid gap-5 sm:grid-cols-2">
            <FormInput id="income" label="Monthly Income (Rs.)" type="number" placeholder="75000" required {...c('monthlyIncome')} />
            <FormInput id="company" label="Company / Business Name" placeholder="Infosys Ltd." required {...c('companyName')} />
            <FormInput id="exp" label="Work Experience (Years)" type="number" placeholder="5" required {...c('experience')} />
        </div>
    );
}

function Step3({ d, u }) {
    function c(k) { return { value: d[k], onChange: function (e) { u(k, e.target.value); } }; }
    return (
        <div className="grid gap-5 sm:grid-cols-2">
            <FormSelect id="propType" label="Property Type" required options={[{ value: 'flat', label: 'Flat / Apartment' }, { value: 'house', label: 'Independent House' }, { value: 'plot', label: 'Plot / Land' }]} {...c('propertyType')} />
            <FormInput id="propVal" label="Property Value (Rs.)" type="number" placeholder="6000000" required {...c('propertyValue')} />
            <FormInput id="loanAmt" label="Loan Amount Required (Rs.)" type="number" placeholder="4500000" required {...c('loanAmount')} />
            <FormInput id="propLoc" label="Property Location" placeholder="Andheri West, Mumbai" required {...c('propertyLocation')} />
        </div>
    );
}

function Step4({ files, onFile }) {
    var docs = [
        { key: 'panCard', label: 'PAN Card', accept: '.pdf,.jpg,.jpeg,.png' },
        { key: 'aadhaarCard', label: 'Aadhaar Card', accept: '.pdf,.jpg,.jpeg,.png' },
        { key: 'salarySlips', label: 'Salary Slips (Last 3 Months)', accept: '.pdf' },
        { key: 'bankStatement', label: 'Bank Statement (6 Months)', accept: '.pdf' },
        { key: 'propertyDocs', label: 'Property Documents', accept: '.pdf' },
        { key: 'photograph', label: 'Photograph', accept: '.jpg,.jpeg,.png' },
    ];
    return (
        <div className="grid sm:grid-cols-2 gap-4">
            {docs.map(function (d) {
                return <FileUploadField key={d.key} label={d.label} accept={d.accept} file={files[d.key]} onChange={function (e) { onFile(d.key, e.target.files[0]); }} />;
            })}
        </div>
    );
}

function Step5({ basic, income, property }) {
    var rows = [
        ['Full Name', basic.fullName], ['Mobile', basic.mobile], ['Email', basic.email],
        ['PAN', basic.pan], ['Employment', basic.employmentType],
        ['Monthly Income', income.monthlyIncome ? 'Rs. ' + Number(income.monthlyIncome).toLocaleString('en-IN') : '-'],
        ['Company', income.companyName], ['Experience', income.experience ? income.experience + ' yrs' : '-'],
        ['Property Type', property.propertyType],
        ['Property Value', property.propertyValue ? 'Rs. ' + Number(property.propertyValue).toLocaleString('en-IN') : '-'],
        ['Loan Amount', property.loanAmount ? 'Rs. ' + Number(property.loanAmount).toLocaleString('en-IN') : '-'],
        ['Location', property.propertyLocation],
    ];
    return (
        <div className="space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">Please review your details before submitting.</p>
            <div className="rounded-xl border border-slate-100 dark:border-slate-800 overflow-hidden">
                <table className="w-full text-sm"><tbody>
                    {rows.map(function (row, i) {
                        return (
                            <tr key={row[0]} className={i % 2 === 0 ? 'bg-slate-50 dark:bg-slate-800/40' : 'bg-white dark:bg-slate-900'}>
                                <td className="px-4 py-2.5 font-medium text-slate-600 dark:text-slate-400 w-1/2">{row[0]}</td>
                                <td className="px-4 py-2.5 text-slate-900 dark:text-slate-100">{row[1] || '-'}</td>
                            </tr>
                        );
                    })}
                </tbody></table>
            </div>
            <p className="text-xs text-slate-400 dark:text-slate-500">By submitting you agree to our Terms and Privacy Policy.</p>
        </div>
    );
}

/* ── Success screen ───────────────────────────────────────────────────────── */

function SuccessScreen() {
    return (
        <div className="flex flex-col items-center justify-center py-16 text-center space-y-5">
            <div className="h-24 w-24 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center animate-bounce">
                <CheckCircle2 className="h-12 w-12 text-emerald-500" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white font-['Plus_Jakarta_Sans']">Application Submitted!</h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-sm">
                Your <strong>Home Loan</strong> application has been received. Our team will reach out within 24 to 48 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
                <Button asChild className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"><a href="/loan-products">Explore More Loans</a></Button>
                <Button variant="outline" asChild className="rounded-xl"><a href="/">Back to Home</a></Button>
            </div>
        </div>
    );
}

/* ── Main Page ───────────────────────────────────────────────────────────── */

var STEPS = ['Basic Details', 'Income Details', 'Property Details', 'Documents', 'Review & Submit'];

var HomeLoanForm = function () {
    var sb = useState({ fullName: '', mobile: '', email: '', dob: '', pan: '', aadhaar: '', employmentType: '' });
    var basic = sb[0]; var setBasic = sb[1];

    var si = useState({ monthlyIncome: '', companyName: '', experience: '' });
    var income = si[0]; var setIncome = si[1];

    var sp = useState({ propertyType: '', propertyValue: '', loanAmount: '', propertyLocation: '' });
    var property = sp[0]; var setProperty = sp[1];

    var sf = useState({});
    var files = sf[0]; var setFiles = sf[1];

    var sc = useState(0);
    var current = sc[0]; var setCurrent = sc[1];

    var ss = useState(false);
    var submitted = ss[0]; var setSubmitted = ss[1];

    function updBasic(k, v) { setBasic(function (p) { var n = Object.assign({}, p); n[k] = v; return n; }); }
    function updIncome(k, v) { setIncome(function (p) { var n = Object.assign({}, p); n[k] = v; return n; }); }
    function updProperty(k, v) { setProperty(function (p) { var n = Object.assign({}, p); n[k] = v; return n; }); }
    function updFile(k, f) { setFiles(function (p) { var n = Object.assign({}, p); n[k] = f; return n; }); }

    var isLast = current === STEPS.length - 1;

    function handleNext() {
        if (isLast) { setSubmitted(true); return; }
        setCurrent(function (p) { return p + 1; });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    function handleBack() {
        setCurrent(function (p) { return p - 1; });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    var stepContent;
    if (current === 0) stepContent = <Step1 d={basic} u={updBasic} />;
    else if (current === 1) stepContent = <Step2 d={income} u={updIncome} />;
    else if (current === 2) stepContent = <Step3 d={property} u={updProperty} />;
    else if (current === 3) stepContent = <Step4 files={files} onFile={updFile} />;
    else stepContent = <Step5 basic={basic} income={income} property={property} />;

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pb-24 -mt-16">
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white pt-32 pb-16">
                <div className="container mx-auto px-4 max-w-6xl">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="h-12 w-12 rounded-2xl bg-emerald-500/20 flex items-center justify-center">
                            <Home className="h-6 w-6 text-emerald-400" />
                        </div>
                        <div>
                            <p className="text-emerald-400 text-sm font-medium uppercase tracking-widest">Apply Online</p>
                            <h1 className="text-3xl md:text-4xl font-bold font-['Plus_Jakarta_Sans']">Home Loan Application</h1>
                        </div>
                    </div>
                    <p className="text-slate-300 max-w-xl ml-16">Get approval in 48 hours. Interest starting from <strong className="text-emerald-400">8.5% p.a.</strong></p>
                </div>
            </div>

            <div className="container mx-auto px-4 max-w-6xl mt-10">
                {submitted ? <SuccessScreen /> : (
                    <div className="grid lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 space-y-8">
                            <ProgressBar steps={STEPS} current={current} />
                            <div className="rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-md p-6 md:p-8 space-y-6">
                                <div className="border-b border-slate-100 dark:border-slate-800 pb-4">
                                    <h3 className="text-lg font-bold text-slate-900 dark:text-white font-['Plus_Jakarta_Sans']">{STEPS[current]}</h3>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Fields marked with <span className="text-rose-500">*</span> are required.</p>
                                </div>
                                {stepContent}
                                <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-800">
                                    <Button variant="outline" onClick={handleBack} disabled={current === 0} className="rounded-xl gap-1.5"><ChevronLeft className="h-4 w-4" />Back</Button>
                                    <Button onClick={handleNext} className="rounded-xl gap-1.5 bg-slate-900 hover:bg-slate-800 dark:bg-emerald-600 dark:hover:bg-emerald-700 text-white px-6">
                                        {isLast ? 'Submit Application' : 'Continue'}
                                        {isLast ? <CheckCircle2 className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-6">
                            <EMICalc />
                            <div className="rounded-2xl border border-blue-100 dark:border-blue-900/40 bg-blue-50/40 dark:bg-blue-900/10 p-5 space-y-3">
                                <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-400">Eligibility Check</h4>
                                <ul className="space-y-2 text-xs text-slate-600 dark:text-slate-400">
                                    <li className="flex items-start gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-blue-500 mt-0.5 shrink-0" />Age: 21 to 65 years</li>
                                    <li className="flex items-start gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-blue-500 mt-0.5 shrink-0" />Min. Monthly Income: Rs. 25,000</li>
                                    <li className="flex items-start gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-blue-500 mt-0.5 shrink-0" />CIBIL Score 700 or above</li>
                                    <li className="flex items-start gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-blue-500 mt-0.5 shrink-0" />Valid PAN and Aadhaar required</li>
                                </ul>
                            </div>
                            <div className="rounded-2xl border border-amber-100 dark:border-amber-900/40 bg-amber-50/40 dark:bg-amber-900/10 p-5 space-y-2">
                                <h4 className="text-sm font-semibold text-amber-700 dark:text-amber-400">Need Help?</h4>
                                <p className="text-xs text-slate-600 dark:text-slate-400">Speak to our loan expert for personalised guidance.</p>
                                <p className="text-sm font-bold text-amber-700 dark:text-amber-400">1800-XXX-XXXX</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default HomeLoanForm;
